import React, { useState, useMemo, useEffect } from "react";
import { Card, Tag, Button, Segmented, Table, Divider, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  TrophyOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Row, Col } from "reactstrap";
import tenderApis from "../../../api/tender/tenderApis";
import BAFODetailsDrawer from "./BAFODetailsDrawer";
import {
  colors as avatarColors,
  getSupplierInitials,
  formatMYR,
} from "./components/comparisonUtils";

const rankColors: Record<number, { bg: string; color: string; label: string }> =
  {
    1: { bg: "#fef3c7", color: "#a16207", label: "1st" },
    2: { bg: "#f3f4f6", color: "#6b7280", label: "2nd" },
    3: { bg: "#ffedd5", color: "#c2410c", label: "3rd" },
  };

const getStatusColor = (status: string) => {
  switch (status) {
    case "Shortlisted":
      return "green";
    case "Under Review":
      return "orange";
    case "Rejected":
      return "red";
    default:
      return "default";
  }
};

const BAFOView = ({ tenderID }: { tenderID?: string }) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [allSuppliers, setAllSuppliers] = useState<any[]>([]);
  const [evaluationMap, setEvaluationMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const tenderId = Number(tenderID);

        // Fetch BAFO bid responses and evaluation results in parallel
        const [bidResponse, evalResponse] = await Promise.all([
          tenderApis.fetchBafoBidResponses(tenderId),
          tenderApis.getTenderEvaluationResults(tenderId),
        ]);

        // Process BAFO bid responses as supplier list
        let supplierList: any[] = [];
        if (bidResponse?.status || bidResponse?.data) {
          const responseData = bidResponse?.data?.response || bidResponse?.data || {};
          supplierList = responseData?.suppliers || responseData?.list || [];
        }
        const mapped = supplierList.map((s: any) => ({
          ...s,
          supplierID: String(s.supplierId || s.supplierID || s.contractorId || s.bidId),
          supplierName: s.supplierName || s.contractorName || "",
          lineItems: s.lineItems || s.materials || s.bidMaterials || s.items || [],
        }));
        setAllSuppliers(mapped);

        // Process evaluation results
        let evalSuppliers: any[] = [];
        if (evalResponse?.status || evalResponse?.data) {
          const evalData = evalResponse?.data?.response || evalResponse?.data || {};
          evalSuppliers = evalData?.supplierEvaluations || [];
        }
        const map: Record<string, any> = {};
        evalSuppliers.forEach((supplier: any) => {
          const supplierId = supplier.supplierId || supplier.supplierID || supplier.bidId;
          map[supplierId] = {
            ...supplier,
            supplierID: supplierId,
          };
        });
        setEvaluationMap(map);
      } catch (error) {
        console.log("Error fetching BAFO data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (tenderID) fetchData();
  }, [tenderID]);

  const awardedSuppliers = useMemo(
    () =>
      allSuppliers.filter(
        (s: any) => s.status === "Shortlisted" || s.status === "Awarded"
      ),
    [allSuppliers]
  );
  const otherSuppliers = useMemo(
    () =>
      allSuppliers.filter(
        (s: any) => s.status !== "Shortlisted" && s.status !== "Awarded"
      ),
    [allSuppliers]
  );

  const sectionNames = useMemo(() => {
    const sections = new Set<string>();
    Object.values(evaluationMap).forEach((supplier: any) => {
      (supplier.sections || []).forEach((section: any) => {
        sections.add(section.sectionName);
      });
    });
    return Array.from(sections);
  }, [evaluationMap]);

  const getSupplierSectionScores = (supplierID: string) => {
    const evalData = evaluationMap[supplierID];
    if (!evalData) return { sections: [], overallScore: 0 };
    const sections = evalData.sections.map((section: any) => ({
      name: section.sectionName,
      score: section.sectionScore,
      maxScore: section.maxScore,
    }));
    const overallScore = evalData.overallScore || (
      sections.length > 0
        ? sections.reduce((sum: number, s: any) => sum + s.score, 0) /
          sections.length
        : 0
    );
    return { sections, overallScore };
  };

  const handleViewDetails = (supplier: any) => {
    setSelectedSupplier(supplier);
    setDrawerVisible(true);
  };

  // ========== HERO CARD (Awarded) ==========
  const renderHeroCard = (supplier: any) => {
    const { sections, overallScore } = getSupplierSectionScores(
      supplier.supplierID
    );
    const displayOverall =
      overallScore || supplier.scores?.overallScore || 0;
    const avatarColor =
      avatarColors[
        allSuppliers.findIndex(
          (s: any) => s.supplierID === supplier.supplierID
        ) % avatarColors.length
      ];
    const awardedMats = (supplier.awardedMaterials || []).filter(
      (m: any) => m.status === "Awarded"
    );
    const awardedValue = awardedMats.reduce(
      (sum: number, m: any) => sum + (m.lineTotal || 0),
      0
    );
    const rank = supplier.rank || evaluationMap[supplier.supplierID]?.rank;
    const rankStyle = rank
      ? rankColors[rank] || {
          bg: "#f5f5f5",
          color: "#888",
          label: `${rank}th`,
        }
      : { bg: "#f5f5f5", color: "#888", label: "N/A" };

    return (
      <Card
        key={supplier.supplierID}
        style={{
          marginBottom: 16,
          border: "2px solid #52c41a",
          borderRadius: 10,
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Green banner */}
        <div
          style={{
            background: "#52c41a",
            color: "#fff",
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 6,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <CheckCircleOutlined />
          Awarded Supplier
        </div>

        <div style={{ padding: "16px 20px" }}>
          <Row>
            {/* Left — identity + awarded stats */}
            <Col lg="5" md="12">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: avatarColor,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {getSupplierInitials(supplier.supplierName)}
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a" }}>
                    {supplier.supplierName}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                    <Tag color="green">{supplier.status}</Tag>
                    <Tag
                      style={{
                        background: rankStyle.bg,
                        color: rankStyle.color,
                        border: "none",
                        fontWeight: 600,
                      }}
                    >
                      <TrophyOutlined style={{ marginRight: 3 }} />
                      {rankStyle.label}
                    </Tag>
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
                <span>Awarded Materials: </span>
                <strong>{awardedMats.length}</strong>
              </div>
              <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>
                <span>Total Value: </span>
                <strong style={{ color: "#389e0d" }}>
                  INR {formatMYR(awardedValue)}
                </strong>
              </div>
              {/* <div style={{ fontSize: 13, color: "#555", marginBottom: 14 }}>
                <span>Valid Till: </span>
                <strong>{supplier.validityDate}</strong>
              </div> */}

              <Button
                type="primary"
                icon={<EyeOutlined />}
                style={{ background: "#52c41a", borderColor: "#52c41a" }}
                onClick={() => handleViewDetails(supplier)}
              >
                View Awarded Details
              </Button>
            </Col>

            {/* Right — scores */}
            <Col lg="7" md="12">
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 8,
                }}
              >
                Evaluation Scores
              </div>
              {sections.length > 0 ? (
                sections.map((s: any) => (
                  <div
                    key={s.name}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "4px 0",
                      borderBottom: "1px solid #f0f0f0",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: "#555" }}>{s.name}</span>
                    <span style={{ fontWeight: 500 }}>
                      {s.score}/{s.maxScore}
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ color: "#bbb", fontSize: 12 }}>
                  No evaluation data
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 0 0",
                  fontWeight: 600,
                  color: "#1890ff",
                  fontSize: 13,
                }}
              >
                <span>Overall Score</span>
                <span>{displayOverall.toFixed(1)}/100</span>
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    );
  };

  // ========== SIMPLE CARD (Others) ==========
  const renderOtherCard = (supplier: any) => {
    const { sections, overallScore } = getSupplierSectionScores(
      supplier.supplierID
    );
    const rank = supplier.rank || evaluationMap[supplier.supplierID]?.rank;
    const rankStyle = rank
      ? rankColors[rank] || {
          bg: "#f5f5f5",
          color: "#888",
          label: `${rank}th`,
        }
      : { bg: "#f5f5f5", color: "#888", label: "N/A" };
    const avatarColor =
      avatarColors[
        allSuppliers.findIndex(
          (s: any) => s.supplierID === supplier.supplierID
        ) % avatarColors.length
      ];
    const totalValue = (supplier.awardedMaterials || []).reduce(
      (sum: number, m: any) => sum + (m.lineTotal || 0),
      0
    );
    const displayOverall =
      overallScore || supplier.scores?.overallScore || 0;

    return (
      <Col lg="4" md="6" sm="12" key={supplier.supplierID} className="mb-3">
        <Card style={{ height: "100%" }} bodyStyle={{ padding: 18 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor: avatarColor,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getSupplierInitials(supplier.supplierName)}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>
                  {supplier.supplierName}
                </div>
                <Tag
                  color={getStatusColor(supplier.status)}
                  style={{ marginTop: 2 }}
                >
                  {supplier.status}
                </Tag>
              </div>
            </div>
            <Tag
              style={{
                background: rankStyle.bg,
                color: rankStyle.color,
                border: "none",
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
              }}
            >
              <TrophyOutlined style={{ marginRight: 3 }} />
              {rankStyle.label}
            </Tag>
          </div>

          {/* Scores */}
          <div style={{ marginBottom: 10 }}>
            {sections.length > 0 ? (
              sections.map((s: any) => (
                <div
                  key={s.name}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "3px 0",
                    borderBottom: "1px solid #f5f5f5",
                    fontSize: 12,
                  }}
                >
                  <span style={{ color: "#666" }}>{s.name}</span>
                  <span>{s.score}/{s.maxScore}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "#bbb", fontSize: 12, padding: "4px 0" }}>
                No evaluation data
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "4px 0 0",
                fontWeight: 600,
                color: "#1890ff",
                fontSize: 12,
              }}
            >
              <span>Overall</span>
              <span>{displayOverall.toFixed(1)}/100</span>
            </div>
          </div>

          {/* Bottom info */}
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            Value: <strong style={{ color: "#333" }}>INR {formatMYR(totalValue)}</strong>
          </div>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
            Valid Till: {supplier.validityDate}
          </div>

          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            block
            onClick={() => handleViewDetails(supplier)}
          >
            View Details
          </Button>
        </Card>
      </Col>
    );
  };

  // ========== LIST VIEW ==========
  const tableData = useMemo(() => {
    return allSuppliers.map((supplier: any, index: number) => {
      const { sections, overallScore } = getSupplierSectionScores(
        supplier.supplierID
      );
      const displayOverall =
        overallScore || supplier.scores?.overallScore || 0;
      const totalValue = (supplier.awardedMaterials || []).reduce(
        (sum: number, m: any) => sum + (m.lineTotal || 0),
        0
      );
      const row: any = {
        key: supplier.supplierID,
        supplierName: supplier.supplierName,
        supplierIndex: index,
        rank: supplier.rank || evaluationMap[supplier.supplierID]?.rank,
        status: supplier.status,
        isAwarded: supplier.status === "Shortlisted" || supplier.status === "Awarded",
        overallScore: displayOverall,
        totalValue,
        validityDate: supplier.validityDate,
        materialCount: (supplier.awardedMaterials || []).length,
        _raw: supplier,
      };
      sectionNames.forEach((name) => {
        const sec = sections.find((s: any) => s.name === name);
        row[name] = sec ? `${sec.score}/${sec.maxScore}` : "N/A";
      });
      return row;
    });
  }, [allSuppliers, sectionNames, evaluationMap]);

  const listColumns = useMemo((): ColumnsType<any> => {
    const cols: ColumnsType<any> = [
      {
        title: "Supplier",
        dataIndex: "supplierName",
        key: "supplier",
        fixed: "left",
        width: 220,
        render: (_: any, record: any) => {
          const rankStyle = record.rank
            ? rankColors[record.rank] || {
                bg: "#f5f5f5",
                color: "#888",
                label: `${record.rank}th`,
              }
            : { bg: "#f5f5f5", color: "#888", label: "N/A" };
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor:
                    avatarColors[record.supplierIndex % avatarColors.length],
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getSupplierInitials(record.supplierName)}
              </div>
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: 500, color: "#333" }}
                  title={record.supplierName}
                >
                  {record.supplierName}
                </div>
                <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                  <Tag
                    style={{
                      background: rankStyle.bg,
                      color: rankStyle.color,
                      border: "none",
                      fontSize: 10,
                      lineHeight: "16px",
                      padding: "0 5px",
                      fontWeight: 600,
                    }}
                  >
                    <TrophyOutlined style={{ marginRight: 2 }} />
                    {rankStyle.label}
                  </Tag>
                  {record.isAwarded && (
                    <Tag
                      color="green"
                      style={{ fontSize: 10, lineHeight: "16px", padding: "0 5px" }}
                    >
                      Awarded
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 110,
        render: (status: string) => (
          <Tag color={getStatusColor(status)}>{status}</Tag>
        ),
      },
    ];

    sectionNames.forEach((name) => {
      cols.push({
        title: name,
        dataIndex: name,
        key: name,
        align: "center",
        width: 120,
        render: (val: string) => (
          <span style={{ color: val === "N/A" ? "#ccc" : "#333" }}>
            {val}
          </span>
        ),
      });
    });

    cols.push(
      {
        title: "Overall",
        dataIndex: "overallScore",
        key: "overallScore",
        align: "center",
        width: 100,
        render: (v: number) => (
          <strong style={{ color: "#1890ff" }}>{v.toFixed(1)}</strong>
        ),
      },
      {
        title: "Materials",
        dataIndex: "materialCount",
        key: "materialCount",
        align: "center",
        width: 90,
      },
      {
        title: "Total Value",
        dataIndex: "totalValue",
        key: "totalValue",
        align: "right",
        width: 140,
        render: (v: number) => <>INR {formatMYR(v)}</>,
      },
      {
        title: "Valid Till",
        dataIndex: "validityDate",
        key: "validityDate",
        width: 110,
      },
      {
        title: "",
        key: "actions",
        fixed: "right",
        width: 80,
        render: (_: any, record: any) => (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record._raw)}
          >
            View
          </Button>
        ),
      }
    );

    return cols;
  }, [sectionNames, allSuppliers]);

  // ========== RENDER ==========
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 0" }}>
      {/* Toggle */}
      <div style={{ marginBottom: 16 }}>
        <Segmented
          options={[
            { label: "Grid View", value: "grid" },
            { label: "List View", value: "list" },
          ]}
          value={viewMode}
          onChange={(val) => setViewMode(val as "grid" | "list")}
        />
      </div>

      {/* === GRID VIEW === */}
      {viewMode === "grid" && (
        <>
          <Divider orientation="left" style={{ color: "#389e0d", fontSize: 13 }}>
            <CheckCircleOutlined style={{ marginRight: 6 }} />
            Awarded Suppliers ({awardedSuppliers.length})
          </Divider>
          {awardedSuppliers.length > 0 ? (
            awardedSuppliers.map((s: any) => renderHeroCard(s))
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "24px 0",
                color: "#bfbfbf",
                fontSize: 13,
              }}
            >
              No awarded suppliers yet
            </div>
          )}

          {otherSuppliers.length > 0 && (
            <>
              <Divider orientation="left" style={{ color: "#8c8c8c", fontSize: 13 }}>
                <InfoCircleOutlined style={{ marginRight: 6 }} />
                Other Participants
              </Divider>
              <Row>
                {otherSuppliers.map((s: any) => renderOtherCard(s))}
              </Row>
            </>
          )}
        </>
      )}

      {/* === LIST VIEW === */}
      {viewMode === "list" && (
        <Table
          dataSource={tableData}
          columns={listColumns}
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
          rowClassName={(r: any) => (r.isAwarded ? "bafo-awarded-row" : "")}
        />
      )}

      <style>{`
        .bafo-awarded-row td { background: #f6ffed !important; }
        .bafo-awarded-row:hover td { background: #d9f7be !important; }
      `}</style>

      <BAFODetailsDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        supplierName={selectedSupplier?.supplierName || ""}
        awardedMaterials={selectedSupplier?.awardedMaterials || []}
      />
    </div>
  );
};

export default BAFOView;
